import { useState, useRef, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react'
import './ExpansionPanel.css'

const ExpansionPanel = forwardRef(function ExpansionPanel(
  { children, startExpanded = false, onToggle, className = '' },
  ref
) {
  const [expanded, setExpanded] = useState(startExpanded)
  const panelRef = useRef(null)
  const measureRef = useRef(null)
  const rafRef = useRef(null)

  const syncHeight = useCallback(() => {
    if (!panelRef.current || !measureRef.current) return
    const contentHeight = measureRef.current.scrollHeight
    panelRef.current.style.height = expanded ? `${contentHeight}px` : '0px'
  }, [expanded])

  useEffect(() => {
    syncHeight()

    if (expanded) {
      const check = () => {
        syncHeight()
        rafRef.current = requestAnimationFrame(check)
      }
      rafRef.current = requestAnimationFrame(check)
      const timeout = setTimeout(() => {
        if (panelRef.current) {
          panelRef.current.style.transitionDuration = '25ms'
        }
      }, 300)
      return () => {
        cancelAnimationFrame(rafRef.current)
        clearTimeout(timeout)
      }
    } else if (panelRef.current) {
      panelRef.current.style.transitionDuration = '300ms'
    }
  }, [expanded, syncHeight])

  const expand = useCallback(() => {
    setExpanded(true)
    onToggle?.(true)
  }, [onToggle])

  const contract = useCallback(() => {
    setExpanded(false)
    onToggle?.(false)
  }, [onToggle])

  const toggle = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev
      onToggle?.(next)
      return next
    })
  }, [onToggle])

  useImperativeHandle(ref, () => ({ expand, contract, toggle, expanded }), [
    expand,
    contract,
    toggle,
    expanded,
  ])

  return (
    <div
      ref={panelRef}
      className={`expansion-panel ${expanded ? '' : 'expansion-panel--collapsed'} ${className}`}
    >
      <div ref={measureRef} className="expansion-panel__inner">
        {children}
      </div>
    </div>
  )
})

export default ExpansionPanel
